import React, { useEffect, useState } from 'react';
import {
  Typography,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer
} from '@mui/material';
import PlayerCharts from '../components/PlayerCharts';

const PerformancePage = () => {
  const [weeklyData, setWeeklyData] = useState([]);
  const [drillData, setDrillData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ weeks: [], drills: [] });
  const [selectedWeek, setSelectedWeek] = useState('');
  const [selectedDrill, setSelectedDrill] = useState('');
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('user_id');

  const fetchFilteredData = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedWeek) params.append('week', selectedWeek);
      if (selectedDrill) params.append('drill', selectedDrill);

      const [weeklyRes, drillRes, summaryRes] = await Promise.all([
        fetch(`http://localhost:8000/workout-log/${userId}/weekly-summary?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`http://localhost:8000/workout-log/${userId}/drill-summary?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`http://localhost:8000/workout-log/${userId}/filtered-summary?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setWeeklyData(await weeklyRes.json());
      setDrillData(await drillRes.json());
      setSummary(await summaryRes.json());
    } catch (err) {
      console.error('Failed to fetch filtered data:', err);
    }
  };

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const [weeklyRes, drillRes, summaryRes] = await Promise.all([
          fetch(`http://localhost:8000/workout-log/${userId}/weekly-summary`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`http://localhost:8000/workout-log/${userId}/drill-summary`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`http://localhost:8000/workout-log/${userId}/summary`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        setWeeklyData(await weeklyRes.json());
        setDrillData(await drillRes.json());
        setSummary(await summaryRes.json());
      } catch (err) {
        console.error('Failed to fetch performance data:', err);
      } finally {
        setLoading(false);
      }
    };

    const fetchOptions = async () => {
      try {
        const res = await fetch(`http://localhost:8000/workout-log/${userId}/filter-options`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setFilterOptions(data);
      } catch (err) {
        console.error('Failed to fetch filter options:', err);
      }
    };

    if (userId && token) {
      fetchInitial();
      fetchOptions();
    }
  }, [userId, token]);

  const computeWeeklyDiffs = (data) => {
    const result = [];
    let prev = null;

    data.forEach((week) => {
      const curr = { ...week };
      if (prev) {
        curr.makes_diff = curr.total_makes - prev.total_makes;
        curr.attempts_diff = curr.total_attempts - prev.total_attempts;
        const currPct = curr.shooting_pct || 0;
        const prevPct = prev.shooting_pct || 0;
        curr.accuracy_diff = parseFloat(((currPct - prevPct) * 100).toFixed(2));
      } else {
        curr.makes_diff = null;
        curr.attempts_diff = null;
        curr.accuracy_diff = null;
      }
      result.push(curr);
      prev = week;
    });

    return result;
  };

  const styledCell = (value, isPct = false) => {
    const isPositive = value > 0;
    const isNegative = value < 0;
    const color = isPositive ? '#d5f5e3' : isNegative ? '#f8d7da' : 'inherit';
    const display = value === null || isNaN(value) ? '--' : isPct ? `${value.toFixed(2)}%` : value;
    return <TableCell sx={{ backgroundColor: color }}>{display}</TableCell>;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const tableData = computeWeeklyDiffs(weeklyData);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Performance
      </Typography>
      <Typography variant="body1" gutterBottom>
        View your shooting percentages and progress over time here.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 2, mb: 3 }}>
        <FormControl fullWidth sx={{ maxWidth: 350 }}>
          <InputLabel>Week</InputLabel>
          <Select
            value={selectedWeek}
            label="Week"
            onChange={(e) => setSelectedWeek(e.target.value)}
          >
            <MenuItem value=""><em>All</em></MenuItem>
            {filterOptions.weeks?.map((week) => (
              <MenuItem key={week} value={week}>{week}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ maxWidth: 350 }}>
          <InputLabel>Drill</InputLabel>
          <Select
            value={selectedDrill}
            label="Drill"
            onChange={(e) => setSelectedDrill(e.target.value)}
          >
            <MenuItem value=""><em>All</em></MenuItem>
            {filterOptions.drills?.map((drill) => (
              <MenuItem key={drill} value={drill}>{drill}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="contained" onClick={fetchFilteredData} sx={{ maxWidth: 200 }}>
          Apply Filter
        </Button>
      </Box>

      {summary && (
        <Box sx={{ mb: 3 }}>
          <Typography><strong>Total Drills Logged:</strong> {summary.drills_completed ?? 0}</Typography>
          <Typography><strong>Total Makes:</strong> {summary.total_makes ?? 0}</Typography>
          <Typography><strong>Total Attempts:</strong> {summary.total_attempts ?? 0}</Typography>
          <Typography><strong>Shooting %:</strong> {summary.shooting_pct ?? 0}</Typography>
          <Typography><strong>Last Submission:</strong> {summary.last_submission_date ?? 'N/A'}</Typography>
        </Box>
      )}

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#000', color: '#fff' }}>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Week</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Drills Completed</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Makes</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Attempts</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Accuracy %</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Δ Makes</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Δ Attempts</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Δ Accuracy %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData.map((row, idx) => (
              <TableRow key={idx} sx={{ backgroundColor: idx % 2 === 0 ? '#f9f9f9' : '#ffffff', '&:hover': { backgroundColor: '#f0f0f0' } }}>
                <TableCell>{row.week_number}</TableCell>
                <TableCell>{row.drills_completed}</TableCell>
                <TableCell>{row.total_makes}</TableCell>
                <TableCell>{row.total_attempts}</TableCell>
                <TableCell>{((row.shooting_pct ?? 0) * 100).toFixed(2)}%</TableCell>
                {styledCell(row.makes_diff)}
                {styledCell(row.attempts_diff)}
                {styledCell(row.accuracy_diff, true)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography sx={{ mt: 1, fontStyle: 'italic', color: '#555' }}>
        Δ values show changes compared to the previous week’s metrics.
      </Typography>

      <PlayerCharts weeklyData={weeklyData} drillData={drillData} />
    </Box>
  );
};

export default PerformancePage;
